import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { ScanOutData, ScanOutItem } from '../types';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import SignaturePad from './SignaturePad';

interface ScanOutModalProps {
  orderId: string;
  scanType: 'will_call' | 'freight';
  onComplete: (pickedUpBy?: string) => void;
  onClose: () => void;
}

type Step = 'scan' | 'accessories' | 'signature' | 'confirm';

interface VerifiedPackage {
  package_id: string;
  verified_at: string;
  method: 'scan' | 'manual';
}

export default function ScanOutModal({ orderId, scanType, onComplete, onClose }: ScanOutModalProps) {
  const [data, setData] = useState<ScanOutData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState<Step>('scan');
  // V1 state
  const [verifiedItems, setVerifiedItems] = useState<ScanOutItem[]>([]);
  // V2 state
  const [verifiedPackages, setVerifiedPackages] = useState<VerifiedPackage[]>([]);
  // Shared state
  const [accessoriesConfirmed, setAccessoriesConfirmed] = useState(false);
  const [pickedUpBy, setPickedUpBy] = useState('');
  const [signedBy, setSignedBy] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [scanError, setScanError] = useState('');

  const isV2 = data?.has_packages === true;

  useEffect(() => {
    loadData();
  }, [orderId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await api.getScanOutData(orderId);
      setData(result);
      if (result.accessories.length === 0) {
        setAccessoriesConfirmed(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scan-out data');
    } finally {
      setLoading(false);
    }
  };

  // ========== V1 helpers ==========
  const isItemVerified = (itemNumber: number) => {
    return verifiedItems.some(v => v.item_number === itemNumber);
  };

  const verifyItem = useCallback((itemNumber: number, method: 'scan' | 'manual') => {
    setVerifiedItems(prev => {
      if (prev.some(v => v.item_number === itemNumber)) return prev;
      return [...prev, {
        item_number: itemNumber,
        verified_at: new Date().toISOString(),
        method,
      }];
    });
    setScanError('');
  }, []);

  // ========== V2 helpers ==========
  const isPackageVerified = (pkgId: string) => {
    return verifiedPackages.some(v => v.package_id === pkgId);
  };

  const verifyPackage = useCallback((pkgId: string, method: 'scan' | 'manual') => {
    setVerifiedPackages(prev => {
      if (prev.some(v => v.package_id === pkgId)) return prev;
      return [...prev, {
        package_id: pkgId,
        verified_at: new Date().toISOString(),
        method,
      }];
    });
    setScanError('');
  }, []);

  // ========== Barcode handler (dual mode) ==========
  const handleBarcodeScan = useCallback((barcode: string) => {
    if (!data) return;

    if (isV2 && data.packages) {
      // V2: Parse {order}-{item}-{pkg} barcode
      const match = barcode.trim().match(/^(.+)-(\d+)-(\d+)$/);
      if (!match) {
        setScanError(`Invalid package barcode format: "${barcode}". Expected format: ORDER-ITEM-PKG`);
        return;
      }

      const [, scannedOrder, itemNumStr, pkgNumStr] = match;
      const currentOrder = (data.order_number || '').toUpperCase();

      if (scannedOrder.toUpperCase() !== currentOrder) {
        setScanError(`Wrong order! Scanned "${scannedOrder}" but expected "${data.order_number}"`);
        return;
      }

      const itemNum = parseInt(itemNumStr, 10);
      const pkgNum = parseInt(pkgNumStr, 10);
      const pkg = data.packages.find(p => p.item_number === itemNum && p.package_number === pkgNum);

      if (!pkg) {
        setScanError(`Package not found: Screen #${itemNum}, Pkg ${pkgNum}`);
        return;
      }

      if (isPackageVerified(pkg.id)) {
        setScanError(`Already verified: Screen #${itemNum}, Pkg ${pkgNum}`);
        return;
      }

      verifyPackage(pkg.id, 'scan');
    } else {
      // V1: Legacy — barcode is just order number
      const scannedOrder = barcode.trim().toUpperCase();
      const currentOrder = (data.order_number || '').toUpperCase();

      if (scannedOrder !== currentOrder) {
        setScanError(`Wrong barcode! Scanned "${barcode}" but expected "${data.order_number}"`);
        return;
      }

      const nextUnverified = data.items.find(item => !isItemVerified(item.item_number));
      if (nextUnverified) {
        verifyItem(nextUnverified.item_number, 'scan');
      } else {
        setScanError('All items already verified');
      }
    }
  }, [data, isV2, verifiedItems, verifiedPackages, verifyItem, verifyPackage]);

  const { inputRef } = useBarcodeScanner(handleBarcodeScan);

  // Progress calculations
  const allVerified = isV2
    ? (data?.packages ? verifiedPackages.length >= data.packages.length : false)
    : (data ? verifiedItems.length >= data.items.length : false);

  const verifiedCount = isV2 ? verifiedPackages.length : verifiedItems.length;
  const totalCount = isV2 ? (data?.packages?.length || 0) : (data?.items?.length || 0);

  const handleNextFromScan = () => {
    if (data && data.accessories.length === 0) {
      if (scanType === 'will_call') {
        setStep('signature');
      } else {
        setStep('confirm');
      }
    } else {
      setStep('accessories');
    }
  };

  const handleNextFromAccessories = () => {
    if (scanType === 'will_call') {
      setStep('signature');
    } else {
      setStep('confirm');
    }
  };

  const handleSubmit = async () => {
    if (!data) return;
    setSubmitting(true);
    setError('');

    try {
      const payload: any = {
        scan_type: scanType,
        scanned_items: verifiedItems,
        accessories_confirmed: accessoriesConfirmed,
        signature_data: signatureData || undefined,
        signed_by: signedBy || undefined,
        picked_up_by: pickedUpBy || undefined,
        notes: notes || undefined,
      };

      if (isV2) {
        payload.scanned_packages = verifiedPackages;
      }

      await api.saveScanOut(orderId, payload);
      onComplete(scanType === 'will_call' ? pickedUpBy : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scan-out');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const stepLabels: Record<Step, string> = {
    scan: isV2 ? 'Verify Packages' : 'Verify Items',
    accessories: 'Accessories',
    signature: 'Signature',
    confirm: 'Confirm',
  };

  const steps: Step[] = scanType === 'will_call'
    ? (data.accessories.length > 0 ? ['scan', 'accessories', 'signature', 'confirm'] : ['scan', 'signature', 'confirm'])
    : (data.accessories.length > 0 ? ['scan', 'accessories', 'confirm'] : ['scan', 'confirm']);

  const currentStepIndex = steps.indexOf(step);

  // Group packages by item_number for v2 display
  const packagesByItem: Record<number, typeof data.packages> = {};
  if (isV2 && data.packages) {
    for (const pkg of data.packages) {
      if (!packagesByItem[pkg.item_number]) packagesByItem[pkg.item_number] = [];
      packagesByItem[pkg.item_number]!.push(pkg);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      {/* Hidden barcode scanner input */}
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
      />

      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 rounded-t-xl z-10">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-white">Scan-Out Verification</h2>
              <p className="text-gray-400 text-sm">
                {data.order_number} — {data.dealer_company}
                {data.sidemark ? ` — ${data.sidemark}` : ''}
                {isV2 && <span className="ml-2 text-indigo-400 text-xs">(per-package)</span>}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step progress */}
          <div className="flex items-center gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <div className={`h-0.5 w-6 ${i <= currentStepIndex ? 'bg-indigo-500' : 'bg-gray-600'}`} />}
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                  s === step ? 'bg-indigo-600 text-white' :
                  i < currentStepIndex ? 'bg-green-600 text-white' :
                  'bg-gray-700 text-gray-400'
                }`}>
                  {i < currentStepIndex && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {stepLabels[s]}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          {error && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 mb-4">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Scan */}
          {step === 'scan' && (
            <div>
              {/* Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">
                    {verifiedCount}/{totalCount} {isV2 ? 'packages' : 'screens'} verified
                  </span>
                  {allVerified && (
                    <span className="text-green-400 text-sm font-medium flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      All verified
                    </span>
                  )}
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${allVerified ? 'bg-green-500' : 'bg-indigo-500'}`}
                    style={{ width: `${totalCount > 0 ? (verifiedCount / totalCount) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {scanError && (
                <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 mb-4 animate-pulse">
                  <p className="text-red-300 text-sm font-medium">{scanError}</p>
                </div>
              )}

              <p className="text-gray-400 text-sm mb-4">
                {isV2
                  ? `Scan each package barcode (e.g., ${data.order_number}-1-1) or use "Verify" button.`
                  : `Scan each screen's barcode label (${data.order_number}) or use "Verify" button as fallback.`
                }
              </p>

              {/* V2: Package list grouped by screen */}
              {isV2 && data.packages ? (
                <div className="space-y-4">
                  {data.items.map((item) => {
                    const itemPkgs = packagesByItem[item.item_number] || [];
                    const verifiedInItem = itemPkgs.filter(p => isPackageVerified(p.id)).length;
                    const allItemVerified = verifiedInItem === itemPkgs.length;

                    return (
                      <div key={item.item_number}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-sm font-medium ${allItemVerified ? 'text-green-300' : 'text-white'}`}>
                            Screen #{item.item_number}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {Number(item.max_width).toFixed(1)}" x {Number(item.max_height).toFixed(1)}" — {item.system_color}
                          </span>
                          <span className={`ml-auto text-xs ${allItemVerified ? 'text-green-400' : 'text-gray-400'}`}>
                            {verifiedInItem}/{itemPkgs.length} pkgs
                          </span>
                        </div>
                        <div className="space-y-1.5 ml-2">
                          {itemPkgs.map((pkg) => {
                            const verified = isPackageVerified(pkg.id);
                            const entry = verifiedPackages.find(v => v.package_id === pkg.id);
                            return (
                              <div
                                key={pkg.id}
                                className={`flex items-center justify-between p-2.5 rounded-lg border transition-all ${
                                  verified
                                    ? 'bg-green-900/30 border-green-700'
                                    : 'bg-gray-750 border-gray-600'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                    verified ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                                  }`}>
                                    {verified ? (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    ) : (
                                      pkg.package_number
                                    )}
                                  </div>
                                  <span className={`text-sm ${verified ? 'text-green-300' : 'text-white'}`}>
                                    Pkg {pkg.package_number} of {itemPkgs.length}
                                  </span>
                                  <span className="text-gray-500 text-xs">{pkg.barcode}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {verified ? (
                                    <span className="text-green-400 text-xs">
                                      {entry?.method === 'scan' ? 'Scanned' : 'Manual'}
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => verifyPackage(pkg.id, 'manual')}
                                      className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                                    >
                                      Verify
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* V1: Item list (legacy) */
                <div className="space-y-2">
                  {data.items.map((item) => {
                    const verified = isItemVerified(item.item_number);
                    const verifiedEntry = verifiedItems.find(v => v.item_number === item.item_number);
                    return (
                      <div
                        key={item.item_number}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          verified
                            ? 'bg-green-900/30 border-green-700'
                            : 'bg-gray-750 border-gray-600'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            verified ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                          }`}>
                            {verified ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              item.item_number
                            )}
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${verified ? 'text-green-300' : 'text-white'}`}>
                              Screen #{item.item_number}
                            </p>
                            <p className="text-gray-400 text-xs">
                              {Number(item.max_width).toFixed(1)}" x {Number(item.max_height).toFixed(1)}" — {item.system_color} — {item.fabric_type}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {verified ? (
                            <span className="text-green-400 text-xs">
                              {verifiedEntry?.method === 'scan' ? 'Scanned' : 'Manual'}
                            </span>
                          ) : (
                            <button
                              onClick={() => verifyItem(item.item_number, 'manual')}
                              className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                              Verify
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleNextFromScan}
                  disabled={!allVerified}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Accessories */}
          {step === 'accessories' && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Verify Accessories</h3>

              {data.accessories.length === 0 ? (
                <div className="bg-gray-750 rounded-lg p-6 text-center mb-4">
                  <p className="text-gray-300">No accessories for this order</p>
                  <p className="text-gray-500 text-sm mt-1">Auto-confirmed</p>
                </div>
              ) : (
                <div className="space-y-2 mb-4">
                  {data.accessories.map((acc, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-750 rounded-lg border border-gray-600">
                      <div>
                        <p className="text-white text-sm font-medium">{acc.description}</p>
                        <p className="text-gray-400 text-xs">{acc.accessory_type}</p>
                      </div>
                      <span className="text-white font-medium">x{acc.quantity}</span>
                    </div>
                  ))}
                </div>
              )}

              <label className="flex items-center gap-3 p-4 bg-gray-750 rounded-lg border border-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accessoriesConfirmed}
                  onChange={(e) => setAccessoriesConfirmed(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-500 text-green-500 focus:ring-green-500"
                />
                <span className="text-white font-medium">
                  {data.accessories.length > 0
                    ? 'Accessories bag verified and included'
                    : 'No accessories — confirmed'
                  }
                </span>
              </label>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setStep('scan')}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleNextFromAccessories}
                  disabled={!accessoriesConfirmed}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Signature (will-call only) */}
          {step === 'signature' && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Customer Signature</h3>

              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Picked Up By *</label>
                  <input
                    type="text"
                    value={pickedUpBy}
                    onChange={(e) => setPickedUpBy(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Name of person picking up"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    value={signedBy}
                    onChange={(e) => setSignedBy(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Customer name for signature"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Signature *</label>
                  <SignaturePad onSave={setSignatureData} />
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => data.accessories.length > 0 ? setStep('accessories') : setStep('scan')}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep('confirm')}
                  disabled={!pickedUpBy.trim() || !signedBy.trim() || !signatureData}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {step === 'confirm' && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Confirm & Submit</h3>

              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-3 p-3 bg-green-900/30 rounded-lg border border-green-700">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-300">
                    {isV2 ? (
                      <>
                        {verifiedPackages.length}/{data.packages?.length || 0} packages verified
                        ({verifiedPackages.filter(v => v.method === 'scan').length} scanned, {verifiedPackages.filter(v => v.method === 'manual').length} manual)
                      </>
                    ) : (
                      <>
                        {verifiedItems.length}/{data.items.length} screens verified
                        ({verifiedItems.filter(v => v.method === 'scan').length} scanned, {verifiedItems.filter(v => v.method === 'manual').length} manual)
                      </>
                    )}
                  </span>
                </div>

                <div className="flex items-center gap-3 p-3 bg-green-900/30 rounded-lg border border-green-700">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-300">
                    {data.accessories.length > 0 ? 'Accessories confirmed' : 'No accessories (confirmed)'}
                  </span>
                </div>

                {scanType === 'will_call' && (
                  <div className="flex items-center gap-3 p-3 bg-green-900/30 rounded-lg border border-green-700">
                    <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-green-300">
                      Signature captured — {signedBy} (picked up by {pickedUpBy})
                    </span>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => {
                    if (scanType === 'will_call') {
                      setStep('signature');
                    } else if (data.accessories.length > 0) {
                      setStep('accessories');
                    } else {
                      setStep('scan');
                    }
                  }}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Processing...
                    </span>
                  ) : (
                    `Complete Scan-Out`
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
